import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, CheckCircle, XCircle, Building2, AlertCircle } from 'lucide-react';
import { 
  useLinkedInTempOrganizations, 
  useConnectLinkedInPages 
} from '../../hooks/useLinkedInTempOrganizations';
import type { LinkedInOrganization } from '../../services/linkedinService';
import { getCountryFlagEmoji } from '../../utils/countryEmojis';

interface LinkedInOrganizationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onError?: (error: string) => void;
}

export const LinkedInOrganizationSelectionModal: React.FC<LinkedInOrganizationSelectionModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  onError
}) => {
  const { t } = useTranslation();
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  
  const { 
    data: orgData, 
    isLoading: isLoadingOrgs, 
    error: fetchError 
  } = useLinkedInTempOrganizations(isOpen);
  
  const connectMutation = useConnectLinkedInPages();

  const organizations = orgData?.organizations || [];

  // Don't auto-select any organizations by default
  // User must manually choose which ones to connect

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedOrganizations([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOrganizationToggle = (orgId: string) => {
    if (connectMutation.isPending) return;
    
    setSelectedOrganizations(prev => 
      prev.includes(orgId) 
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const handleSelectAll = () => {
    if (connectMutation.isPending) return;
    setSelectedOrganizations(organizations.map(org => org.id));
  };

  const handleDeselectAll = () => {
    if (connectMutation.isPending) return;
    setSelectedOrganizations([]);
  };

  const handleConnect = async () => {
    if (selectedOrganizations.length === 0) {
      onError?.(t('accounts.linkedin.selectAtLeastOne'));
      return;
    }

    try {
      await connectMutation.mutateAsync({
        selectedPageIds: selectedOrganizations
      });
      onComplete();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('accounts.linkedin.connectionFailed');
      onError?.(errorMessage);
    }
  };

  const getOrganizationDisplayName = (org: LinkedInOrganization): string => {
    let displayName = '';
    if (org.localizedName && org.localizedName !== `Organization ${org.id}`) {
      displayName = org.localizedName;
    } else {
      displayName = t('accounts.linkedin.linkedInPageFallback', { id: org.id });
    }
    
    // Add country flag emoji if location information is available
    if (org.locations && org.locations.length > 0) {
      const primaryLocation = org.locations[0]; // Use the first location as primary
      const countryEmoji = getCountryFlagEmoji(primaryLocation.country);
      displayName += ` ${countryEmoji}`;
    }
    
    return displayName;
  };

  const getOrganizationTypeInfo = (type: string): { emoji: string; label: string; bgColor: string; textColor: string } => {
    const typeMap: Record<string, { emoji: string; labelKey: string; bgColor: string; textColor: string }> = {
      'EDUCATIONAL': { emoji: '📚', labelKey: 'accounts.linkedin.organizationType.educational', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
      'PUBLIC_COMPANY': { emoji: '📈', labelKey: 'accounts.linkedin.organizationType.publicCompany', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
      'PRIVATELY_HELD': { emoji: '🏢', labelKey: 'accounts.linkedin.organizationType.privatelyHeld', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
      'SELF_EMPLOYED': { emoji: '🤓', labelKey: 'accounts.linkedin.organizationType.selfEmployed', bgColor: 'bg-blue-50', textColor: 'text-blue-600' },
      'SELF_OWNED': { emoji: '💼', labelKey: 'accounts.linkedin.organizationType.selfOwned', bgColor: 'bg-teal-100', textColor: 'text-teal-700' },
      'PARTNERSHIP': { emoji: '🤝', labelKey: 'accounts.linkedin.organizationType.partnership', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
      'NON_PROFIT': { emoji: '❤️', labelKey: 'accounts.linkedin.organizationType.nonProfit', bgColor: 'bg-red-100', textColor: 'text-red-700' },
      'GOVERNMENT_AGENCY': { emoji: '🏛️', labelKey: 'accounts.linkedin.organizationType.governmentAgency', bgColor: 'bg-green-100', textColor: 'text-green-700' }
    };

    const typeInfo = typeMap[type] || { emoji: '🏢', labelKey: 'accounts.linkedin.organizationType.unknown', bgColor: 'bg-gray-100', textColor: 'text-gray-700' };
    return {
      emoji: typeInfo.emoji,
      label: t(typeInfo.labelKey),
      bgColor: typeInfo.bgColor,
      textColor: typeInfo.textColor
    };
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden border-2 border-black">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('accounts.linkedin.organizationSelectionTitle')}
              </h2>
            </div>
            <button
              onClick={onClose}
              disabled={connectMutation.isPending}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {isLoadingOrgs && (
              <div className="flex items-center justify-center py-8">
                                 <div className="text-center">
                   <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
                   <p className="text-gray-600">{t('accounts.linkedin.loadingOrganizations')}</p>
                 </div>
              </div>
            )}

            {fetchError && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <XCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
                  <p className="text-red-600 mb-2">{t('accounts.linkedin.loadFailed')}</p>
                  <p className="text-gray-500 text-sm">{fetchError.message}</p>
                </div>
              </div>
            )}

            {!isLoadingOrgs && !fetchError && organizations.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <AlertCircle className="w-8 h-8 mx-auto mb-4 text-yellow-500" />
                  <p className="text-gray-600">{t('accounts.linkedin.noOrganizationsFound')}</p>
                  <p className="text-gray-500 text-sm">{t('accounts.linkedin.noOrganizationsFoundSubtext')}</p>
                </div>
              </div>
            )}

            {!isLoadingOrgs && !fetchError && organizations.length > 0 && (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    {t('accounts.linkedin.selectionDescription')}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                    <div className="flex gap-2">
                      <button
                        onClick={handleSelectAll}
                        disabled={connectMutation.isPending}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        {t('accounts.linkedin.selectAll')}
                      </button>
                      <button
                        onClick={handleDeselectAll}
                        disabled={connectMutation.isPending}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        {t('accounts.linkedin.deselectAll')}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">
                      {t('accounts.linkedin.organizationsSelected', { 
                        count: selectedOrganizations.length, 
                        total: organizations.length 
                      })}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {organizations.map((org) => {
                    const isSelected = selectedOrganizations.includes(org.id);
                    const isDisabled = connectMutation.isPending;
                    const orgTypeInfo = getOrganizationTypeInfo(org.organizationType);
                    
                    return (
                      <div
                        key={org.id}
                        onClick={() => !isDisabled && handleOrganizationToggle(org.id)}
                                                                         className={`p-4 border rounded-xl cursor-pointer transition-all ${
                          isSelected
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-black hover:border-gray-300'
                        } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center gap-4">
                                                     <div className="flex-shrink-0">
                             {isSelected ? (
                               <CheckCircle className="w-5 h-5 text-primary-600" />
                             ) : (
                               <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                             )}
                           </div>
                          
                                                     <div className="flex items-center gap-3 flex-1">
                                                         <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 truncate">
                                {getOrganizationDisplayName(org)}
                              </h3>
                                                             <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                                                 <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                  {t('accounts.linkedin.companyPage')}
                                </span>
                                <span className={`px-2 py-0.5 ${orgTypeInfo.bgColor} ${orgTypeInfo.textColor} rounded text-xs flex items-center gap-1`}>
                                  <span>{orgTypeInfo.emoji}</span>
                                  <span>{orgTypeInfo.label}</span>
                                </span>
                               </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {!isLoadingOrgs && !fetchError && organizations.length > 0 && (
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">
                {orgData?.expiresAt && (
                  t('accounts.linkedin.organizationsExpire', { 
                    date: new Date(orgData.expiresAt).toLocaleDateString() 
                  })
                )}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={connectMutation.isPending}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
                                 <button
                   onClick={handleConnect}
                   disabled={selectedOrganizations.length === 0 || connectMutation.isPending}
                   className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                 >
                  {connectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {connectMutation.isPending 
                    ? t('accounts.linkedin.connecting')
                    : t('accounts.linkedin.connectButton', { count: selectedOrganizations.length })
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 